import { jwtDecode } from "jwt-decode";
import axios from "axios";
import config from "../../config";
import { UserSession } from "redux/types";

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.API_URL;

// intercepting to capture errors
axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;


    // Case 1️⃣: No response received at all (network issue / CORS / timeout)
    if (!error.response) {
      return Promise.reject({
        message: "network-err",
        description: "No response received from server. Please check your connection.",
      });
    }

    // Case 2️⃣: Gateway timeout or upstream timeout (504)
    if (error?.response?.status === 504) {
      return Promise.reject({
        message: "gateway-timeout",
        description: "The request took too long — backend might still be processing.",
      });
    }

    if (error?.response?.status === 502) {
      return Promise.reject({
        message: "bad-gateway",
        description: "502 Bad Gateway",
      });
    }

    // case 3️⃣: Received response with error status code
    if (error && error.response && error.response.status === 404) {
      // window.location.href = '/not-found';
      return Promise.reject(
        {
          statusCode: error.response.status,
          error: error.response.statusText
        }
      )
    } else if (error && error.response && error.response.status === 403) {
      if (error.response.data.error.message !== "err-session-already-exists-for-user") {
        window.location.href = "/access-denied";
      }
    } else {
      switch (error.response.status) {
        case 401:
          if (sessionStorage.getItem("vayana_lms_user")) {
            clearSessionStorage();
            window.location.href = "/account/logout"; //logout logic here
          } else {
            //window.location.href = "/account/login";
          }

          message = error.response.data.error ? error.response?.data?.error : error.message || "Invalid credentials";
          break;
        case 400: {
          const apiError = error.response?.data?.error;

          if (
            apiError?.type === "ValidationError" && apiError?.args?.errors?.length > 0
          ) {
            message = {
              ...apiError,
              message: apiError.args.errors
                .map((validationError: any) => validationError.message)
                .join(", "),
            };
          } else {
            message = apiError || error.message || error;
          }

          break;
        }

        case 403:
          if (error.response.data.error.message !== "err-session-already-exists-for-user") {
            window.location.href = "/access-denied";
            message = "Access Forbidden";
          }
          break;
        case 404:
          message = "Sorry! the data you are looking for could not be found";
          break;
        default: {
          message = error.response.data.error && error ? error.response?.data?.error : error.message || error;
        }
      }
      return Promise.reject(message);
    }
    return Promise.reject(error.response.data.error);
  }
);

const AUTH_SESSION_KEY = "vayana_lms_user";
export const SESSION_EXPIRY_TIME = "session_expiry";
export const SESSION_AUTH_ID = "sessionAuthId";

/** VITE_USER_SESSION_DURATION_IN_MINS is often a string; it can be missing or 0 — must not yield 0 duration. */
export const getUserSessionDurationMs = (): number => {
  const raw = config.USER_SESSION_DURATION_IN_MINS;
  const mins = typeof raw === "string" ? parseFloat(raw.trim()) : Number(raw);
  const m = Number.isFinite(mins) && mins > 0 ? mins : 60;
  return m * 60000;
};

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (session: UserSession | null) => {
  if (session) {
    // axios.defaults.headers.common['Authorization'] = 'JWT ' + token;
    axios.defaults.headers.common["Authorization"] = "Bearer " + session.token;
    axios.defaults.headers.common["financierId"] = session.financierId;
  } else {
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["financierId"];
  }
};



const clearSessionStorage = () => {
  // 1. Remove stored session
  sessionStorage.removeItem(AUTH_SESSION_KEY);
  sessionStorage.removeItem("isOtpVerified");
  sessionStorage.removeItem(SESSION_EXPIRY_TIME);
  sessionStorage.removeItem(SESSION_AUTH_ID);
  setAuthorization(null);
};

const getSessionExpirty = () => {
  let sessionExpiry = sessionStorage.getItem(SESSION_EXPIRY_TIME)
  if (!sessionExpiry) {
    return 0;
  }

  let currentTime = Date.now();
  let timeInterval = Number(sessionExpiry) - currentTime;
  const msInMinute = 60 * 1000;

  if (timeInterval <= 0) {
    return 0;
  }

  return Math.floor(timeInterval / msInMinute)

}
const getTokenExpiryDiff = () => {
  let sessionData: any = sessionStorage.getItem("vayana_lms_user");
  if (!sessionData) {
    return 0;
  }

  try {
    const user = JSON.parse(sessionData);
    const decoded: any = jwtDecode(user.token);
    let timeInterval = decoded.exp * 1000 - Date.now();
    const msInMinute = 60 * 1000;

    if (timeInterval <= 0) {
      return 0;
    }

    return Math.floor(timeInterval / msInMinute);
  } catch {
    return 0;
  }
};

const getUserFromSession = (): UserSession | null => {
  const user = sessionStorage.getItem(AUTH_SESSION_KEY);
  return user ? (typeof user === "object" ? user : JSON.parse(user)) : null;
};

const getUserPermissions = () => {
  try {
    const user = sessionStorage.getItem(AUTH_SESSION_KEY);
    const userObject = user ? JSON.parse(user) : null;
    return userObject?.permissions || null;
  } catch (error) {
    console.error("Failed to parse user data:", error);
    return null;
  }
};

/*
Check if token available in session
*/
let user = getUserFromSession();
if (user) {
  setAuthorization(user);
}

class APICore {
  /**
   * Fetches data from given url
   */
  refreshTokenIfExpired = async () => {
    let isTokenRefreshed = false;
    if (sessionStorage.getItem("vayana_lms_user") !== "" && sessionStorage.getItem("vayana_lms_user") !== null) {
      let getExpiryDiff = getTokenExpiryDiff();
      if (getExpiryDiff <= 2 && getExpiryDiff > 0) {
        let response = await axios.put(`${config.BACKEND_BASE_URL}/login`, {});
        this.setLoggedInUser(response.data.data);
        setAuthorization(response.data.data);

        isTokenRefreshed = true;
      }
    }

    return isTokenRefreshed;
  };

  setUserSessionAfterSSORedirect = async (sessionAuthId: string) => {
    if (!sessionAuthId?.trim()) {
      return false;
    }

    try {
      const response = await axios.put(
        `${config.BACKEND_BASE_URL}/sso/session-auth-token`,
        { sessionAuthId: sessionAuthId.trim() }
      );

      this.setLoggedInUser(response.data.data);
      setAuthorization(response.data.data);
      sessionStorage.setItem(SESSION_EXPIRY_TIME, (Date.now() + getUserSessionDurationMs()).toString());

      return true;
    } catch (error) {
      console.error("SSO session-auth-token exchange failed:", error);
      return false;
    }
  };


  get = async (url: string, params: any) => {
    let response;
    let filterData: any;
    let moreFilters: any = [];

    if (params) {
      await this.refreshTokenIfExpired();

      var queryString = Object.keys(params).map(key => {
        if (key === "sortField") {
        }
        if (key !== "filters") {
          filterData = key + "=" + params[key];
        } else {
          Object.keys(params["filters"]).map(filter => {
            params[key][filter].forEach((element: any) => {
              filterData = key + "[" + filter + "]" + "[$" + element["matchMode"] + "]" + "=" + encodeURIComponent(element["value"]);
              moreFilters.push(filterData);
            });
          });
          filterData = moreFilters.join("&");
        }
        return filterData;
      });
      response = axios.get(`${url}?${queryString.join("&")}`, params);

      // var queryString = params ? Object.keys(params).map((key) => key + '=' + params[key]).join('&') : '';

    } else {
      response = axios.get(`${url}`, params);
    }
    return response;
  };

  getFile = async (url: string, params: any) => {
    let response;
    if (params) {
      await this.refreshTokenIfExpired();
      var queryString = params
        ? Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, { responseType: "blob" });
    } else {
      response = axios.get(`${url}`, { responseType: "blob" });
    }
    return response;
  };

  getMultiple = async (urls: string, params: any) => {
    const reqs = [];
    let queryString = "";
    if (params) {
      await this.refreshTokenIfExpired();
      queryString = params
        ? Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&")
        : "";
    }

    for (const url of urls) {
      reqs.push(axios.get(`${url}?${queryString}`));
    }
    return axios.all(reqs);
  };

  /**
   * post given data to url
   */
  create = async (url: string, data: any) => {
    await this.refreshTokenIfExpired();

    if (data instanceof FormData) {
      const config: any = {
        headers: {
          ...axios.defaults.headers,
          "content-type": "multipart/form-data"
        }
      };
      return axios.post(url, data, config);
    } 
    return axios.post(url, data);
  };

  createFile = async (url: string, data: any) => {
    await this.refreshTokenIfExpired();
    return axios.post(url, data, { responseType: "blob" });
  };

  /**
   * Updates patch data
   */
  updatePatch = async (url: string, data: any) => {
    await this.refreshTokenIfExpired();
    return axios.patch(url, data);
  };

  /**
   * Updates data
   */
  update = async (url: string, data: any) => {
    if (!url.endsWith("/logout") && !url.endsWith("/login")) {
      await this.refreshTokenIfExpired();
    }
    if (data instanceof FormData) {
      const config: any = {
        headers: {
          ...axios.defaults.headers,
          "content-type": "multipart/form-data"
        }
      };
      return axios.put(url, data, config);
    }
    return axios.put(url, data);
  };

  /**
   * Deletes data
   */
  delete = async (url: string) => {
    await this.refreshTokenIfExpired();
    return axios.delete(url);
  };

  isUserAuthenticated = () => {
    const user = this.getLoggedInUser();
    if (!user || (user && !user.token)) {
      return false;
    }
    const decoded: any = jwtDecode(user.token);
    const currentTime = Date.now() / 1000;

    if (decoded.exp < currentTime) {
      this.refreshTokenIfExpired();
      console.warn("access token expired");
      sessionStorage.removeItem("vayana_lms_user");
      sessionStorage.removeItem("isOtpVerified");
      sessionStorage.removeItem(SESSION_EXPIRY_TIME);
      sessionStorage.removeItem(SESSION_AUTH_ID);
      return false;
    } else {
      return true;
    }
  };

  setLoggedInUser = (session: UserSession | null) => {
    if (session) {sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    }
    else {
      sessionStorage.removeItem(AUTH_SESSION_KEY);
      sessionStorage.removeItem("isOtpVerified");
      sessionStorage.removeItem(SESSION_EXPIRY_TIME);
      sessionStorage.removeItem(SESSION_AUTH_ID);
    }
  };

  /**
   * Returns the logged in user
   */
  getLoggedInUser = (): UserSession | null => {
    return getUserFromSession();
  };

  getLoggedInUserPermission = () => {
    return getUserPermissions();
  }
}
export { APICore, setAuthorization, getTokenExpiryDiff, clearSessionStorage, getSessionExpirty };
